import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, nivelInfo, descargarExcel } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Cierres } from "@/components/Cierres";
import { PendientesRevisar } from "@/components/PendientesRevisar";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Sparkles, Clock, Trash2, ClipboardCheck, Loader2, CalendarPlus,
  Download, UserCog,
} from "lucide-react";

const DIAS_CORTOS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const ESTADOS = [
  { key: "presente", label: "Presente", cls: "bg-emerald-500 text-white border-emerald-500" },
  { key: "ausente", label: "Ausente", cls: "bg-destructive text-white border-destructive" },
  { key: "reposicion", label: "Reposición", cls: "bg-primary text-primary-foreground border-primary" },
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export default function Calendario() {
  const { user } = useAuth();
  const [mes, setMes] = useState(() => startOfMonth(new Date()));
  const [sesiones, setSesiones] = useState([]);
  const [clases, setClases] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [profes, setProfes] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [filtroSede, setFiltroSede] = useState("todas");
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [asistencia, setAsistencia] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [reposiciones, setReposiciones] = useState([]);
  const [cierres, setCierres] = useState([]);
  const [profeSesion, setProfeSesion] = useState("");
  const [propagar, setPropagar] = useState(false);
  const [guardandoProfe, setGuardandoProfe] = useState(false);
  const [nueva, setNueva] = useState({
    clase_id: "", sede_id: "", profesor_id: "", fecha: iso(new Date()),
    hora_inicio: "17:00", hora_fin: "18:30", estado: "programada", notas: "",
  });
  const [gen, setGen] = useState({ sede_id: "", desde: "", hasta: "" });

  const desde = iso(startOfMonth(mes));
  const hasta = iso(endOfMonth(mes));

  const load = () => {
    const q = new URLSearchParams({ desde, hasta });
    if (filtroSede !== "todas") q.set("sede_id", filtroSede);
    api.get(`/sesiones?${q}`).then(({ data }) => setSesiones(data));
    api.get("/asistencia/resumen").then(({ data }) => setResumen(data));
    api.get("/reposiciones?estado=pendiente").then(({ data }) => setReposiciones(data));
    api.get("/cierres").then(({ data }) => setCierres(data)).catch(() => setCierres([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta, filtroSede]);

  useEffect(() => {
    api.get("/clases").then(({ data }) => setClases(data));
    api.get("/sedes").then(({ data }) => setSedes(data));
    api.get("/profesores").then(({ data }) => setProfes(data));
  }, []);

  const claseName = (id) => clases.find((c) => c.id === id)?.nombre || "Clase";
  const profeName = (id) => {
    const p = profes.find((x) => x.id === id);
    return p ? `${p.nombre} ${p.apellidos}`.trim() : "Sin profesor";
  };
  const sedeName = (id) => sedes.find((s) => s.id === id)?.nombre || "";

  const dias = useMemo(() => {
    const inicio = startOfMonth(mes);
    const offset = (inicio.getDay() + 6) % 7;
    const total = endOfMonth(mes).getDate();
    const celdas = Array.from({ length: offset }, () => null);
    for (let d = 1; d <= total; d += 1)
      celdas.push(new Date(mes.getFullYear(), mes.getMonth(), d));
    return celdas;
  }, [mes]);

  const porDia = useMemo(() => {
    const map = {};
    sesiones.forEach((s) => {
      map[s.fecha] = map[s.fecha] || [];
      map[s.fecha].push(s);
    });
    Object.values(map).forEach((l) => l.sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio)));
    return map;
  }, [sesiones]);

  const crearSesion = async () => {
    if (!nueva.clase_id || !nueva.sede_id) return toast.error("Selecciona clase y sede");
    try {
      await api.post("/sesiones", { ...nueva, profesor_id: nueva.profesor_id || null });
      toast.success("Sesión programada");
      setNuevaOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const generar = async () => {
    if (!gen.desde || !gen.hasta) return toast.error("Indica el rango de fechas");
    setGenerando(true);
    try {
      const { data } = await api.post("/sesiones/generar", {
        desde: gen.desde, hasta: gen.hasta, sede_id: gen.sede_id || null,
      });
      toast.success(`${data.creadas} sesiones generadas de ${data.clases} clases`);
      setGenOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGenerando(false);
    }
  };

  const abrirDetalle = async (s) => {
    setDetalle(s);
    setAsistencia(null);
    setProfeSesion(s.profesor_id || "");
    setPropagar(false);
    const { data } = await api.get(`/sesiones/${s.id}/asistencia`);
    setAsistencia(data);
  };

  // Propaga desde la fecha de la sesión si es futura; nunca antes de hoy.
  const desdeCuando = detalle
    ? (detalle.fecha > iso(new Date()) ? detalle.fecha : iso(new Date()))
    : "";

  const guardarProfesor = async () => {
    setGuardandoProfe(true);
    try {
      await api.patch(`/sesiones/${detalle.id}/profesor`, { profesor_id: profeSesion });
      let extra = "";
      if (propagar) {
        const { data } = await api.post(`/clases/${detalle.clase_id}/profesor/propagar`, {
          profesor_id: profeSesion, aplicar: true, desde: desdeCuando,
        });
        extra = ` · ${data.sesiones_a_cambiar} clase(s) futuras actualizadas`;
        if (data.sustituciones_respetadas)
          extra += ` · ${data.sustituciones_respetadas} sustitución(es) respetadas`;
      }
      toast.success(`Profesor actualizado${extra}`);
      setDetalle((d) => ({ ...d, profesor_id: profeSesion || null, profesor_sustituto: true }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setGuardandoProfe(false);
    }
  };

  const marcar = (alumnoId, estado) =>
    setAsistencia((a) => ({
      ...a,
      alumnos: a.alumnos.map((al) => (al.id === alumnoId ? { ...al, estado } : al)),
    }));

  const guardarAsistencia = async () => {
    setGuardando(true);
    try {
      const registros = asistencia.alumnos.filter((a) => a.estado).map((a) => ({
        alumno_id: a.id, estado: a.estado, nota: a.nota || "",
      }));
      const { data } = await api.post(`/sesiones/${detalle.id}/asistencia`, { registros });
      toast.success(
        `Asistencia guardada · ${data.creditos_generados} reposiciones generadas · ${data.clases_descontadas} clases descontadas`
      );
      setDetalle(null);
      setAsistencia(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const borrarSesion = async (id) => {
    await api.delete(`/sesiones/${id}`);
    toast.success("Sesión eliminada");
    setDetalle(null);
    load();
  };

  const agendarReposicion = async (repoId, sesionId) => {
    try {
      await api.post(`/reposiciones/${repoId}/agendar`, { sesion_id: sesionId });
      toast.success("Reposición agendada");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="calendario-page">
      <PageHeader
        title="Calendario y asistencia"
        subtitle="Programa sesiones, asigna profesor y pasa lista"
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full font-semibold" data-testid="export-asistencia-btn"
              onClick={() => descargarExcel("/reportes/asistencia", "asistencia.xlsx")}>
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" className="rounded-full font-semibold" onClick={() => setGenOpen(true)}
              data-testid="generar-sesiones-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Generar desde horarios
            </Button>
            <Button className="rounded-full font-semibold" onClick={() => setNuevaOpen(true)} data-testid="add-sesion-btn">
              <Plus className="h-4 w-4 mr-2" /> Nueva sesión
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-asistencia">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Asistencia</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.porcentaje_asistencia ?? 0}%</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-presentes">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Presentes</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.totales?.presente ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-ausentes">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Ausencias</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.totales?.ausente ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-reposiciones">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Reposiciones pendientes</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.reposiciones_pendientes ?? 0}</p>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="mes-anterior"
            onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="font-display text-xl font-bold capitalize min-w-[190px] text-center" data-testid="mes-actual">
            {mes.toLocaleDateString("es-ES", { month: "long", year: "numeric" })}
          </p>
          <Button variant="outline" size="sm" className="rounded-lg" data-testid="mes-siguiente"
            onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Select value={filtroSede} onValueChange={setFiltroSede}>
          <SelectTrigger className="sm:w-56 rounded-lg" data-testid="calendario-filter-sede"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas mis sedes</SelectItem>
            {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-2xl p-4 sm:p-6 border-border shadow-sm overflow-x-auto" data-testid="calendario-grid">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {DIAS_CORTOS.map((d) => (
              <p key={d} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold text-center py-2">{d}</p>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {dias.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const key = iso(d);
              const items = porDia[key] || [];
              const hoy = key === iso(new Date());
              const cierre = cierres.find((c) => c.fecha_inicio <= key && key <= c.fecha_fin
                && (filtroSede === "todas" || c.sede_id === filtroSede));
              return (
                <div key={key} data-testid={`dia-${key}`}
                  className={`min-h-[104px] rounded-xl border p-2 transition-colors duration-200 ${
                    hoy ? "border-primary bg-accent/40"
                      : cierre ? "border-amber-500/40 bg-amber-500/10"
                      : "border-border hover:bg-muted/50"
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold ${hoy ? "text-primary" : "text-muted-foreground"}`}>{d.getDate()}</span>
                    <button onClick={() => { setNueva((n) => ({ ...n, fecha: key })); setNuevaOpen(true); }}
                      data-testid={`add-sesion-dia-${key}`} className="opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity duration-200">
                      <CalendarPlus className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>
                  <div className="space-y-1 mt-1.5">
                    {cierre && (
                      <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 leading-tight"
                        data-testid={`festivo-${key}`}>
                        {cierre.nombre}
                      </p>
                    )}
                    {items.map((s) => (
                      <button key={s.id} onClick={() => abrirDetalle(s)} data-testid={`sesion-${s.id}`}
                        className={`w-full text-left rounded-lg px-2 py-1 text-[11px] font-semibold leading-tight transition-transform duration-200 hover:-translate-y-[1px] ${
                          s.estado === "cancelada" ? "bg-muted text-muted-foreground line-through"
                            : s.asistencia_registrada ? "bg-emerald-500/15 text-emerald-600"
                            : "bg-primary/15 text-primary"
                        }`}>
                        {s.hora_inicio} {claseName(s.clase_id)} ({s.inscritos ?? 0})
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {reposiciones.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-2xl font-extrabold mb-1">Reposiciones pendientes</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Créditos por faltas de alumnos con seguro de recuperación y clases dadas de alta a mano.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {reposiciones.map((r) => (
              <Card key={r.id} className="rounded-2xl p-5 border-border shadow-sm" data-testid={`reposicion-${r.id}`}>
                <p className="font-display font-bold">{r.alumno}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Falta del {r.fecha_origen}
                  {r.origen === "manual" ? " · alta manual (cualquier clase)" : ` · ${claseName(r.clase_id)}`}
                  {r.caduca ? ` · válida hasta ${r.caduca}` : ""}
                </p>
                <Select onValueChange={(v) => agendarReposicion(r.id, v)}>
                  <SelectTrigger className="mt-4 rounded-lg" data-testid={`agendar-reposicion-${r.id}`}>
                    <SelectValue placeholder="Agendar en una sesión" />
                  </SelectTrigger>
                  <SelectContent>
                    {sesiones
                      .filter((s) => s.fecha >= iso(new Date()) && s.estado !== "cancelada")
                      .slice(0, 40)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.fecha} {s.hora_inicio} · {claseName(s.clase_id)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Nueva sesión */}
      <Dialog open={nuevaOpen} onOpenChange={setNuevaOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="sesion-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nueva sesión</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Clase</Label>
              <Select value={nueva.clase_id}
                onValueChange={(v) => {
                  const c = clases.find((x) => x.id === v);
                  setNueva((n) => ({ ...n, clase_id: v, sede_id: c?.sede_id || n.sede_id, profesor_id: c?.profesor_id || "" }));
                }}>
                <SelectTrigger data-testid="sesion-clase-select"><SelectValue placeholder="Selecciona clase" /></SelectTrigger>
                <SelectContent>
                  {clases.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} · {sedeName(c.sede_id)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Profesor encargado</Label>
              <Select value={nueva.profesor_id} onValueChange={(v) => setNueva((n) => ({ ...n, profesor_id: v }))}>
                <SelectTrigger data-testid="sesion-profesor-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  {profes.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} {p.apellidos}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Fecha</Label>
                <Input type="date" value={nueva.fecha} onChange={(e) => setNueva((n) => ({ ...n, fecha: e.target.value }))} data-testid="sesion-fecha-input" />
              </div>
              <div className="grid gap-2">
                <Label>Inicio</Label>
                <Input type="time" value={nueva.hora_inicio} onChange={(e) => setNueva((n) => ({ ...n, hora_inicio: e.target.value }))} data-testid="sesion-inicio-input" />
              </div>
              <div className="grid gap-2">
                <Label>Fin</Label>
                <Input type="time" value={nueva.hora_fin} onChange={(e) => setNueva((n) => ({ ...n, hora_fin: e.target.value }))} data-testid="sesion-fin-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Notas</Label>
              <Textarea value={nueva.notas} onChange={(e) => setNueva((n) => ({ ...n, notas: e.target.value }))} data-testid="sesion-notas-input" />
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={crearSesion} data-testid="save-sesion-btn">Programar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generar desde horarios */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="rounded-2xl" data-testid="generar-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Generar sesiones</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <p className="text-sm text-muted-foreground">
              Crea automáticamente las sesiones semanales de cada clase activa según sus horarios. No duplica las existentes.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Desde</Label>
                <Input type="date" value={gen.desde} onChange={(e) => setGen((g) => ({ ...g, desde: e.target.value }))} data-testid="gen-desde-input" />
              </div>
              <div className="grid gap-2">
                <Label>Hasta</Label>
                <Input type="date" value={gen.hasta} onChange={(e) => setGen((g) => ({ ...g, hasta: e.target.value }))} data-testid="gen-hasta-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Sede (opcional)</Label>
              <Select value={gen.sede_id} onValueChange={(v) => setGen((g) => ({ ...g, sede_id: v }))}>
                <SelectTrigger data-testid="gen-sede-select"><SelectValue placeholder="Todas mis sedes" /></SelectTrigger>
                <SelectContent>
                  {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={generar} disabled={generando} data-testid="confirm-generar-btn">
              {generando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalle + pasar lista */}
      <Dialog open={!!detalle} onOpenChange={(v) => { if (!v) { setDetalle(null); setAsistencia(null); } }}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="asistencia-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {detalle && claseName(detalle.clase_id)}
            </DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {detalle.fecha} · {detalle.hora_inicio}–{detalle.hora_fin}</span>
                <Badge variant="outline" className="rounded-full">{sedeName(detalle.sede_id)}</Badge>
                {detalle.profesor_sustituto && (
                  <Badge variant="outline" className="rounded-full border-amber-500/50 text-amber-600"
                    data-testid="sesion-sustituto-badge">
                    Profesor cambiado para este día
                  </Badge>
                )}
              </div>

              <div className="rounded-xl border border-border p-4" data-testid="sesion-profesor-bloque">
                <Label className="flex items-center gap-2 mb-3">
                  <UserCog className="h-4 w-4 text-primary" /> Profesor de esta sesión
                </Label>
                <Select value={profeSesion || "sin"}
                  onValueChange={(v) => setProfeSesion(v === "sin" ? "" : v)}>
                  <SelectTrigger data-testid="sesion-profesor-edit-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[50vh]">
                    <SelectItem value="sin">Sin profesor asignado</SelectItem>
                    {profes.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre} {p.apellidos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                  <Checkbox checked={propagar} onCheckedChange={(v) => setPropagar(!!v)}
                    data-testid="propagar-profesor-check" />
                  <span className="text-xs text-muted-foreground leading-snug">
                    Aplicar también a las clases futuras de <b>{claseName(detalle.clase_id)}</b>{" "}
                    desde el {desdeCuando}. Las sustituciones puntuales ya hechas no se tocan.
                  </span>
                </label>
                <Button variant="outline" size="sm" className="rounded-full mt-3"
                  onClick={guardarProfesor}
                  disabled={guardandoProfe || (profeSesion || "") === (detalle.profesor_id || "")}
                  data-testid="guardar-profesor-sesion-btn">
                  {guardandoProfe ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar profesor"}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Ahora mismo: {profeName(detalle.profesor_id)}. Cambiarlo no altera la lista ni la asistencia.
                </p>
              </div>

              {!asistencia && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              {asistencia && asistencia.alumnos.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="asistencia-sin-alumnos">
                  No hay alumnos matriculados en esta clase.
                </p>
              )}
              {asistencia?.alumnos.map((a) => {
                const nivel = nivelInfo(a.nivel);
                return (
                  <div key={a.id} className="rounded-xl border border-border p-4" data-testid={`asistencia-alumno-${a.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${nivel.dot}`} /> {a.nombre} {a.apellidos}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {a.matricula} · {a.seguro_reposicion ? "Con seguro de recuperación" : `Sin seguro · bono: ${a.clases_disponibles}`}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {ESTADOS.map((e) => (
                          <button key={e.key} onClick={() => marcar(a.id, e.key)}
                            data-testid={`marcar-${e.key}-${a.id}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                              a.estado === e.key ? e.cls : "border-border text-muted-foreground hover:bg-muted"
                            }`}>
                            {e.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {a.estado === "ausente" && (
                      <p className="text-xs mt-3 text-muted-foreground">
                        {a.seguro_reposicion
                          ? "Se generará un crédito de reposición al guardar."
                          : "Se descontará 1 clase del bono (sin derecho a reposición)."}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full text-destructive"
              onClick={() => borrarSesion(detalle.id)} data-testid="delete-sesion-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button className="rounded-full font-semibold" onClick={guardarAsistencia}
              disabled={guardando || !asistencia?.alumnos?.length} data-testid="save-asistencia-btn">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ClipboardCheck className="h-4 w-4 mr-2" /> Guardar asistencia</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Cierres sedes={sedes} onCambio={load} soloLectura={user?.role === "profesor"} />

      {user?.role === "admin" && (
        <PendientesRevisar clases={clases} sedeId={filtroSede} onCambio={load} />
      )}
    </div>
  );
}
